import React from "react";
import clsx from "clsx";
import { Box, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";

import {
  getImageBitmap,
  generateIphoneSplashPreview,
  IphoneSplashPreviewAssets,
} from "../../utils/canvas";

import frame from "../../assets/iphone-frame.jpeg";
import placeholderIcon from "../../assets/placeholder-appicon.png";

const PHONE_DISPLAY_RATIO = 0.5;
const CANVAS_WIDTH = 300;
const CANVAS_HEIGHT = CANVAS_WIDTH / PHONE_DISPLAY_RATIO;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxWidth: theme.spacing(32),
  },

  canvasContainer: {
    width: "100%",
    aspectRatio: `${PHONE_DISPLAY_RATIO} / 1`,
  },

  canvas: {
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    userSelect: "none",
  },
}));

export interface IphoneSplashPreviewCanvasProps {
  className?: string;
  splashUrl: string;
  backgroundColor?: string;
}

export function IphoneSplashPreviewCanvas(
  props: IphoneSplashPreviewCanvasProps,
) {
  const { className, splashUrl, backgroundColor } = props;

  const s = useStyles();
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [frameLoaded, setFrameLoaded] = React.useState(false);
  const frameRef = React.useRef<ImageBitmap | null>(null);

  // Load frame asset once
  React.useEffect(() => {
    const loadFrame = async () => {
      try {
        frameRef.current = await getImageBitmap(frame);
        setFrameLoaded(true);
      } catch (error) {
        console.error("Failed to load iPhone frame:", error);
      }
    };

    loadFrame();
  }, []);

  // Render preview when frame is loaded or when splashUrl/backgroundColor changes
  React.useEffect(() => {
    const renderPreview = async () => {
      if (!frameLoaded || !frameRef.current || !canvasRef.current) return;

      try {
        const splashIcon = await getImageBitmap(splashUrl || placeholderIcon);

        const assets: IphoneSplashPreviewAssets = {
          frameImg: frameRef.current,
          splashIcon,
          backgroundColor,
        };

        await generateIphoneSplashPreview(canvasRef, assets);
      } catch (error) {
        console.error("Failed to render iPhone splash preview:", error);
      }
    };

    renderPreview();
  }, [frameLoaded, splashUrl, backgroundColor]);

  return (
    <Box className={clsx(s.root, className)}>
      <Box className={s.canvasContainer}>
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className={s.canvas}
        />
      </Box>
    </Box>
  );
}
